import { templatesList, type Template } from "@reactive-resume/utils";

import { templateCardUrl } from "@/client/constants/template-images";

export type TemplateDocumentKind = "resume" | "cover-letter";

export type TemplateCategory = "all" | "ats" | "popular" | "creative";

export type LetterPreviewVariant =
  | "minimal"
  | "sidebar"
  | "elegant"
  | "classic"
  | "accent"
  | "corporate";

export type DocumentTemplate = {
  id: string;
  name: string;
  kind: TemplateDocumentKind;
  categories: Exclude<TemplateCategory, "all">[];
  thumbnailUrl?: string;
  letterVariant?: LetterPreviewVariant;
  accentColor?: string;

  /**
   * Identifiant véritable du moteur de template.
   * Présent uniquement pour les modèles de CV.
   */
  templateId?: Template;
};

export type TemplateCategoryOption = {
  label: string;
  value: TemplateCategory;
};

type ResumeTemplateMetadata = {
  name: string;
  categories: Exclude<TemplateCategory, "all">[];
};

export const RESUME_TEMPLATE_CATEGORIES: TemplateCategoryOption[] = [
  {
    label: "Tous les modèles",
    value: "all",
  },
  {
    label: "Compatible ATS",
    value: "ats",
  },
  {
    label: "Populaire",
    value: "popular",
  },
  {
    label: "Créatif",
    value: "creative",
  },
];

export const COVER_LETTER_TEMPLATE_CATEGORIES: TemplateCategoryOption[] = [
  {
    label: "Tous les modèles",
    value: "all",
  },
  {
    label: "Professionnel",
    value: "ats",
  },
  {
    label: "Populaire",
    value: "popular",
  },
  {
    label: "Créatif",
    value: "creative",
  },
];

/**
 * Métadonnées éditoriales facultatives.
 *
 * Le registre technique reste `templatesList`.
 * Lorsqu’un nouveau modèle est ajouté à `templatesList` sans être ajouté
 * ici, il apparaît quand même automatiquement avec un nom généré et la
 * catégorie ATS.
 */
const RESUME_TEMPLATE_METADATA: Partial<Record<Template, ResumeTemplateMetadata>> = {
  "code-clarity": {
    name: "Code Clarity",
    categories: ["ats", "popular"],
  },

  "marketing-pulse": {
    name: "Marketing Pulse",
    categories: ["popular", "creative"],
  },

  "people-first": {
    name: "People First",
    categories: ["ats", "popular"],
  },

  "structural-line": {
    name: "Structural Line",
    categories: ["ats"],
  },

  "clinical-calm": {
    name: "Clinical Calm",
    categories: ["ats", "popular"],
  },

  "sales-focus": {
    name: "Sales Focus",
    categories: ["ats", "popular"],
  },

  "account-balance": {
    name: "Account Balance",
    categories: ["ats"],
  },

  "legal-distinction": {
    name: "Legal Distinction",
    categories: ["ats", "popular"],
  },

  "fiscal-arch": {
    name: "Fiscal Arch",
    categories: ["popular", "creative"],
  },

  "soft-contour": {
    name: "Soft Contour",
    categories: ["creative"],
  },

  "sage-orbit": {
    name: "Sage Orbit",
    categories: ["creative"],
  },

  "azure-frame": {
    name: "Azure Frame",
    categories: ["creative"],
  },

  "sand-profile": { name: "Sand Profile", categories: ["popular", "creative"] },
  "editorial-muse": { name: "Editorial Muse", categories: ["popular", "creative"] },
  "slate-ledger": { name: "Slate Ledger", categories: ["popular", "creative"] },
  "blue-horizon": { name: "Blue Horizon", categories: ["popular", "creative"] },
  "dune-flow": { name: "Dune Flow", categories: ["popular", "creative"] },
  "navy-summit": { name: "Navy Summit", categories: ["popular", "creative"] },
  "silver-ledger": { name: "Silver Ledger", categories: ["popular", "creative"] },
  "royal-line": { name: "Royal Line", categories: ["popular", "creative"] },
  "cobalt-balance": { name: "Cobalt Balance", categories: ["popular", "creative"] },
  "charcoal-monogram": { name: "Charcoal Monogram", categories: ["popular", "creative"] },
  "linen-ledger": { name: "Linen Ledger", categories: ["popular", "creative"] },
  "mono-content": { name: "Mono Content", categories: ["popular", "creative"] },
  "plum-timeline": { name: "Plum Timeline", categories: ["popular", "creative"] },
  "citrus-bloom": { name: "Citrus Bloom", categories: ["popular", "creative"] },
  "forest-frame": {
    name: "Forest Frame",
    categories: ["creative"],
  },

  "brand-mocha": {
    name: "Brand Mocha",
    categories: ["popular", "creative"],
  },

  "project-flow": {
    name: "Project Flow",
    categories: ["popular", "creative"],
  },

  "fresh-start": {
    name: "Fresh Start",
    categories: ["ats", "creative"],
  },

  "blue-wave": {
    name: "Blue Wave",
    categories: ["popular", "creative"],
  },
};

const formatTemplateName = (template: string): string => {
  return template
    .split("-")
    .filter(Boolean)
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

/**
 * Cette liste est générée depuis la source de vérité technique.
 *
 * Ajouter un identifiant dans `templatesList` suffit pour que sa carte
 * apparaisse automatiquement ici, à condition que son image JPG existe.
 */
export const RESUME_TEMPLATES: DocumentTemplate[] = templatesList.map((template) => {
  const metadata = RESUME_TEMPLATE_METADATA[template];

  return {
    id: template,
    templateId: template,
    name: metadata?.name ?? formatTemplateName(template),
    kind: "resume",
    categories: metadata?.categories ?? ["ats"],
    thumbnailUrl: templateCardUrl(template),
  };
});

export const COVER_LETTER_TEMPLATES: DocumentTemplate[] = [
  {
    id: "letter-epure",
    name: "Épure",
    kind: "cover-letter",
    categories: ["ats", "popular"],
    letterVariant: "minimal",
    accentColor: "#4f46e5",
  },
  {
    id: "letter-horizon",
    name: "Horizon",
    kind: "cover-letter",
    categories: ["popular", "creative"],
    letterVariant: "sidebar",
    accentColor: "#173665",
  },
  {
    id: "letter-elan",
    name: "Élan",
    kind: "cover-letter",
    categories: ["ats"],
    letterVariant: "elegant",
    accentColor: "#d79a47",
  },
  {
    id: "letter-classique",
    name: "Classique",
    kind: "cover-letter",
    categories: ["ats"],
    letterVariant: "classic",
    accentColor: "#334155",
  },
  {
    id: "letter-audace",
    name: "Audace",
    kind: "cover-letter",
    categories: ["creative"],
    letterVariant: "accent",
    accentColor: "#10b981",
  },
  {
    id: "letter-executive",
    name: "Executive",
    kind: "cover-letter",
    categories: ["popular"],
    letterVariant: "corporate",
    accentColor: "#172554",
  },
];

export const getTemplatesByKind = (kind: TemplateDocumentKind) => {
  return kind === "resume" ? RESUME_TEMPLATES : COVER_LETTER_TEMPLATES;
};

export const getTemplateCategories = (kind: TemplateDocumentKind) => {
  return kind === "resume" ? RESUME_TEMPLATE_CATEGORIES : COVER_LETTER_TEMPLATE_CATEGORIES;
};

export const filterTemplates = (templates: DocumentTemplate[], category: TemplateCategory) => {
  if (category === "all") {
    return templates;
  }

  return templates.filter((template) => template.categories.includes(category));
};
